import { useOnChange } from "@prismicio/editor-support/React"
import {
  AnimatedElement,
  Box,
  Button,
  CircleIcon,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogContent,
  DialogHeader,
  DialogOptions,
  DialogOptionsItem,
  Skeleton,
  SliceSelectCard,
  SuspenseImage,
  Text
} from "@prismicio/editor-ui"
import type { SharedSlice } from "@prismicio/types-internal/lib/customtypes"
import { Fragment, Suspense, useState } from "react"

import { usePromptToSliceFlag } from "../../../app/integrations/AmplitudeExperiments"
import {
  CreateAiSlices,
  CreateAiSlicesHeader,
  RefineSlice,
  useCreateAiSlices,
  useRefineSlice
} from "./aiSlices"
import {
  getLegacySliceConfigs,
  getSharedSliceConfigs,
  type SectionSliceConfig,
  type SliceConfigWithLegacySlice,
  type SliceConfigWithSharedSlice,
  type VisualSharedSlice
} from "./sliceTableData"
import { getFieldset } from "./utils/getFieldset"

export interface SelectSliceDialogProps {
  open: boolean
  sliceConfigs: SectionSliceConfig[]
  mode: Mode
  onCloseRequest: () => void
  onSelect: (slice: SectionSliceConfig | SliceConfigWithSharedSlice) => void
  onSelectPromptedSlice: (promptedSlice: {
    slice: VisualSharedSlice
    promptId: string
  }) => void
  onSelectSmartCopyPaste: () => void
}

type Mode = { name: "add" } | { name: "replace"; sliceId: string }

export function SelectSliceDialog(props: SelectSliceDialogProps) {
  const {
    open,
    mode,
    onCloseRequest,
    sliceConfigs,
    onSelect,
    onSelectPromptedSlice,
    onSelectSmartCopyPaste
  } = props
  const legacySlices = getLegacySliceConfigs({ sliceConfigs })
  const sharedSlices = getSharedSliceConfigs({ sliceConfigs })

  const [option, setOption] = useState(initialOption(mode, sharedSlices))

  const shownSharedSlices = sharedSlices.filter(
    // Filter sharedSlices
    // only show the type of slice on a given index
    // show all sharedSlices if option is allSlicesIndex
    (_, index) => option === allSlicesIndex || index === option
  )

  const createAiSlices = useCreateAiSlices({ slices: shownSharedSlices })
  const refineSlice = useRefineSlice()

  function onInitRefineSlice(args: {
    promptId: string
    slice: VisualSharedSlice
  }) {
    refineSlice.init(args)
  }

  function onSelectRefinedSlice() {
    const { selectedVersion: { promptId, slice } = {} } = refineSlice
    if (!promptId || !slice) throw new Error("Invalid refined slice.")
    onSelectPromptedSlice({ promptId, slice })
  }

  const isRefining = Boolean(refineSlice.selectedVersion)

  useOnChange(open, () => {
    if (open) {
      setOption(initialOption(mode, sharedSlices))
      createAiSlices.clear()
      refineSlice.clear()
    }
  })

  function onOptionChange(value: string) {
    setOption(Number(value))
  }

  function onOpenChange(open: boolean) {
    if (!open) onCloseRequest()
  }

  const promptToSliceEnabled = usePromptToSliceFlag({ active: open }) !== "off"

  const createAiSlicesHeaderShown =
    promptToSliceEnabled &&
    mode.name === "add" &&
    shownSharedSlices.length > 0 &&
    option !== allLegacySlicesIndex &&
    !isRefining

  const createAiSlicesIdle = createAiSlices.status === "idle"

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {createAiSlicesIdle ? (
        <DialogHeader icon="add" title={dialogTitle(mode)} />
      ) : (
        <DialogHeader
          icon="arrowBack"
          onIconClick={isRefining ? refineSlice.clear : createAiSlices.clear}
          title={`Back to ${isRefining ? "prompted slices" : "slice library"}`}
        />
      )}

      {createAiSlicesIdle && (
        <DialogOptions
          value={String(option)}
          onValueChange={onOptionChange}
          footer={
            promptToSliceEnabled && (
              <Button
                color="grey"
                size="large"
                startIcon="contentCopy"
                onClick={onSelectSmartCopyPaste}
              >
                AI smart copy paste
              </Button>
            )
          }
        >
          <DialogOptionsItem
            value={String(allSlicesIndex)}
            title="All slices"
            subtitle={allSlicesSubtitle(sharedSlices)}
          />

          {sharedSlices.map((config, index) => (
            <DialogOptionsItem
              key={index}
              value={`${index}`}
              title={config.sliceConfig.name}
              subtitle={getVariationCountLabel(
                config.sliceConfig.variations.length
              )}
            />
          ))}

          {legacySlices.length > 0 && mode.name !== "replace" && (
            <DialogOptionsItem
              value={String(allLegacySlicesIndex)}
              title="All legacy slices"
              subtitle={`${legacySlices.length} slices`}
            />
          )}
        </DialogOptions>
      )}

      <DialogContent>
        <Box
          overflowY="auto"
          flexGrow={1}
          flexDirection="column"
          padding={24}
          gap={16}
        >
          {createAiSlicesHeaderShown && (
            <CreateAiSlicesHeader {...createAiSlices} />
          )}
          <AnimatedElement enterDuration={150}>
            <Fragment key={option}>
              {isRefining ? (
                <RefineSlice {...refineSlice} />
              ) : (
                <CreateAiSlices
                  {...createAiSlices}
                  onSelectSlice={onInitRefineSlice}
                />
              )}
              {createAiSlicesIdle && (
                <>
                  {shownSharedSlices.map((config) => (
                    <SliceVariations
                      key={config.sliceConfig.id}
                      sliceConfig={config.sliceConfig}
                      onSelect={(sliceVariation) =>
                        onSelect({ ...config, sliceVariation })
                      }
                    />
                  ))}
                  {option === allLegacySlicesIndex && (
                    <LegacySlices
                      onSelect={(config) => onSelect(config)}
                      sliceConfigs={legacySlices}
                    />
                  )}
                </>
              )}
            </Fragment>
          </AnimatedElement>
        </Box>
        {isRefining && (
          <DialogActions>
            <DialogActionButton onClick={onSelectRefinedSlice}>
              Use this slice
            </DialogActionButton>
          </DialogActions>
        )}
      </DialogContent>
    </Dialog>
  )
}

function dialogTitle(mode: Mode) {
  switch (mode.name) {
    case "add":
      return "Select a slice to add"
    case "replace":
      return "Select a variation"
  }
}

function initialOption(mode: Mode, configs: SliceConfigWithSharedSlice[]) {
  switch (mode.name) {
    case "add":
      return allSlicesIndex
    case "replace":
      return (
        configs.findIndex((config) => config.sliceConfig.id === mode.sliceId) ??
        allSlicesIndex
      )
  }
}

interface SliceVariationsProps {
  sliceConfig: SharedSlice
  onSelect: (variationId: string) => void
}

function SliceVariations(props: SliceVariationsProps) {
  const { sliceConfig, onSelect } = props

  return (
    <Box flexDirection="column" gap={16} padding={{ bottom: 24 }}>
      <Text variant="h3" color="grey12">
        {sliceConfig.name}
        <Text variant="h4" color="grey11">
          {" "}
          ({getVariationCountLabel(sliceConfig.variations.length)})
        </Text>
      </Text>

      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={16}>
        {sliceConfig.variations.map((variation) => (
          <SliceSelectCard
            key={variation.name}
            label={sliceConfig.name}
            variation={variation.name}
            onClick={() => onSelect(variation.id)}
          >
            <Suspense
              fallback={
                <Skeleton
                  color="grey1"
                  borderRadius={4}
                  height={100}
                  width="80%"
                />
              }
            >
              <SuspenseImage
                src={variation.imageUrl}
                alt={variation.description}
                sizing="contain"
                boxShadow="3"
              />
            </Suspense>
          </SliceSelectCard>
        ))}
      </Box>
    </Box>
  )
}

interface LegacySlicesProps {
  sliceConfigs: SliceConfigWithLegacySlice[]
  onSelect: (config: SliceConfigWithLegacySlice) => void
}

function LegacySlices(props: LegacySlicesProps) {
  const { sliceConfigs, onSelect } = props

  return (
    <Box flexDirection="column" gap={16} padding={{ bottom: 24 }}>
      <Text variant="h3" color="grey12">
        All legacy slices
      </Text>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={16}>
        {sliceConfigs.map((config, index) => (
          <SliceSelectCard
            key={index}
            label={
              getFieldset({ config: config.sliceConfig }) ??
              config.sliceConfig.type
            }
            onClick={() => onSelect(config)}
          >
            <CircleIcon color="grey" name="viewDay" size="large" />
          </SliceSelectCard>
        ))}
      </Box>
    </Box>
  )
}

const allSlicesIndex = -1
const allLegacySlicesIndex = -2

function allSlicesSubtitle(configs: { sliceConfig: SharedSlice }[]) {
  const variationCount = configs.reduce(
    (total, config) => total + config.sliceConfig.variations.length,
    0
  )

  const sliceLabel = `${configs.length} slice${configs.length === 1 ? "" : "s"}`
  const variationLabel = `${variationCount} Variation${
    variationCount === 1 ? "" : "s"
  }`

  return `${sliceLabel} (${variationLabel})`
}

function getVariationCountLabel(count: number) {
  return `${count} variation${count === 1 ? "" : "s"}`
}
